<template>
  <div class="signup-page">

    <FlashMessage ref="flashMessage" />
    <LoadingIndicator v-if="loading" message="Loading..." />

    <Header />
    <div class="signup-image d-none d-lg-block">
      <img src="@/assets/img/default/login.png" alt="Signup Image" />
    </div>

    <div class="signup-form">
      <div class="form-container">
        <h2>Create Your Account</h2>
        <p>Join us and start your journey!</p>

        <form @submit.prevent="handleSignup">
          <div class="form-group">
            <label for="firstName">First Name</label>
            <input
              type="text"
              id="firstName"
              v-model="formdata.firstName"
              placeholder="Enter your first name"
              required
            />
          </div>

          <div class="form-group">
            <label for="lastName">Last Name</label>
            <input
              type="text"
              id="lastName"
              v-model="formdata.lastName"
              placeholder="Enter your last name"
              required
            />
          </div>

        <div class="form-group">
        <label for="phone">Phone</label>
        <input
          type="text"
          id="phone"
          v-model="formdata.phoneNumber"
          placeholder="e.g. 0683195811"
          required
        />

          <small :class="phoneValid ? 'text-success' : 'text-danger'">
            {{ phoneMessage }}
          </small>
        </div>

          <div class="form-group">
            <label for="email">Email</label>
            <input
              type="email"
              id="email"
              v-model="formdata.email"
              placeholder="Enter your email"
              required
            />
          </div>

        <div class="form-group">
          <label for="password">Password</label>
          <input
            type="password"
            id="password"
            v-model="formdata.password"
            placeholder="Create a password"
            required
          />

          <!-- Validation message -->
          <small :class="passwordValid ? 'text-success' : 'text-danger'">
            {{ passwordMessage }}
          </small>
        </div>

        <div class="form-group">
          <label for="confirmPassword">Confirm Password</label>
          <input
            type="password"
            id="confirmPassword"
            v-model="formdata.confirmPassword"
            placeholder="Confirm your password"
            required
          />

          <!-- Match message -->
          <small :class="passwordMatch ? 'text-success' : 'text-danger'">
            {{ confirmMessage }}
          </small>
        </div>

          <button type="submit" :disabled="!canSubmit" class="signup-button">
          Sign Up
        </button>
        </form>

        <p class="login-link">
          Already have an account?
          <router-link to="/login">Login</router-link>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>


import {
  CustomerRegistration,
} from "../../API-requests/Requests";

import { ref, computed } from "vue";
import Header from "@/components/Header.vue";
import FlashMessage from "@/components/FlashMessage.vue";
import LoadingIndicator from "@/components/Loading_indicator.vue";
import { useRouter } from "vue-router";

const loading = ref(false);
const flashMessage = ref(null);
const error = ref(null);
const router = useRouter();


const formdata = ref({
  firstName:"",
  lastName:"",
  phoneNumber:"",
  email:"",
  password:"",
  confirmPassword: "",
});


// Password length check
const passwordValid = computed(() => {
  return formdata.value.password.length >= 6;
});

// Password match check
const passwordMatch = computed(() => {
  return formdata.value.password === formdata.value.confirmPassword;
});

// Messages
const passwordMessage = computed(() => {
  return passwordValid.value
    ? "Password is valid"
    : "Password must be at least 6 characters";
});

const confirmMessage = computed(() => {
  if (!formdata.value.confirmPassword) return "";
  return passwordMatch.value
    ? "Passwords match"
    : "Passwords do not match";
});


//phone number validator
const phoneValid = computed(() => {
    const phone = formdata.value.phoneNumber;

    if (!phone) return false;

    // Remove spaces
    const cleaned = phone.replace(/\s+/g, "");

    // Local format: 07XXXXXXXX or 06XXXXXXXX
    const localPattern = /^(06|07)\d{8}$/;

    // International: 2557XXXXXXXX or 2556XXXXXXXX
    const intlPattern = /^255(6|7)\d{8}$/;

    return localPattern.test(cleaned) || intlPattern.test(cleaned);
  });

const phoneMessage = computed(() => {
  if (!formdata.value.phoneNumber) return "";

  return phoneValid.value
    ? "Valid phone number"
    : "Enter valid phone (e.g. 0683195811 or 255683195811)";
});

const canSubmit = computed(() => {
  return (
    passwordValid.value &&
    passwordMatch.value &&
    phoneValid.value &&
    formdata.value.password &&
    formdata.value.confirmPassword &&
    formdata.value.phoneNumber
  );
});


const getpayload = ()=>({
  "first_name" : formdata.value.firstName,
	"last_name" : formdata.value.lastName,
	"email" : formdata.value.email,
	"password" : formdata.value.password,
	"phone" : formdata.value.phoneNumber,
});

const handleSignup = async () => {
  try {
    loading.value = true;

    const resp = await CustomerRegistration(getpayload());

    flashMessage.value.showFlashMessage("success", "Succesfuly");
      router.push("/login");
  } catch (err) {
    console.error("Error authenticating user:", err);
    error.value = err.message || "Login failed";
    flashMessage.value.showFlashMessage("failure", error.value);
  } finally {
    loading.value = false;
  }
};

</script>

<style scoped>
.signup-page {
  display: flex;
  min-height: 100vh;
}

.signup-image {
  flex: 1;
  background-color: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.signup-image img {
  max-width: 100%;
  height: 100vh;
  object-fit: cover;
}

.signup-form {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: #fff;
}

.form-container {
  max-width: 400px;
  width: 100%;
}

h2 {
  font-size: 2rem;
  margin-bottom: 10px;
  color: #333;
}

p {
  font-size: 1rem;
  color: #666;
  margin-bottom: 30px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 0.9rem;
  color: #333;
  margin-bottom: 5px;
}

.form-group input {
  width: 100%;
  padding: 10px;
  font-size: 1rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  outline: none;
}

.form-group input:focus {
  border-color: #42b983;
  box-shadow: 0 0 5px rgba(66, 185, 131, 0.5);
}

.signup-button {
  width: 100%;
  padding: 10px;
  font-size: 1rem;
  color: #fff;
  background-color: #42b983;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.signup-button:hover {
  background-color: #3aa876;
}

.login-link {
  font-size: 0.9rem;
  color: #666;
  text-align: center;
  margin-top: 20px;
}

.login-link a {
  color: #42b983;
  text-decoration: none;
}

.login-link a:hover {
  text-decoration: underline;
}

@media (max-width: 992px) {
  .signup-image {
    display: none;
  }

  .signup-form {
    flex: 1;
    padding: 40px 20px;
  }
}
</style>
